import React, { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import ErrorBoundary from './components/ErrorBoundary'
import UpgradeRequiredModal from './components/Common/UpgradeRequiredModal'
import { useUpgradeModalStore } from './stores/upgradeModalStore'
import TierGuard from './components/Common/TierGuard'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import RoleProtectedRoute, { PermissionRoute } from './components/RoleProtectedRoute'
import SuperAdminRoute from './components/SuperAdminRoute'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import { OrionAIProvider } from './contexts/OrionAIContext'
import { OfferteClipboardProvider } from './contexts/OfferteClipboardContext'
import './i18n/config' // Initialize i18n

function PortalQuoteRedirect() {
  const { token } = useParams()
  return <Navigate to={`/portal/view-offer?token=${token ?? ''}`} replace />
}

// Critical path: eager load for fast initial render
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import KlantenPortaalPage from './pages/KlantenPortaalPage'
import DigitaleTweelingDashboard from './components/DigitaleTweelingDashboard'
import AIPlanningPanel from './components/AIPlanningPanel'
import GanttDragDrop from './components/GanttDragDrop'
import DashboardPage from './pages/DashboardPage'
import MobileLayout from './components/mobile/MobileLayout'

// Lazy load pages for faster initial bundle and better code splitting
const KlantenPage = lazy(() => import('./pages/KlantenPage'))
const KlantDetailPage = lazy(() => import('./pages/KlantDetailPage'))
const ProjectenPage = lazy(() => import('./pages/ProjectenPage'))
const ProjectenOverzichtPage = lazy(() => import('./pages/ProjectenOverzichtPage'))
const ProjectenDashboardPage = lazy(() => import('./pages/ProjectenDashboardPage'))
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'))
const DossiersPage = lazy(() => import('./pages/DossiersPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const ServiceoverzichtPage = lazy(() => import('./pages/ServiceoverzichtPage'))
const LeveranciersPage = lazy(() => import('./pages/LeveranciersPage'))
const MerkenKortingenPage = lazy(() => import('./pages/MerkenKortingenPage'))
const ApparatuurPage = lazy(() => import('./pages/ApparatuurPage'))
const OffertesPage = lazy(() => import('./pages/OffertesPage'))
const FacturenPage = lazy(() => import('./pages/FacturenPage'))
const BestellingenPage = lazy(() => import('./pages/BestellingenPage'))
const BestellingDetailPage = lazy(() => import('./pages/BestellingDetailPage'))
const VoorraadPage = lazy(() => import('./pages/VoorraadPage'))
const VoorraadDetailPage = lazy(() => import('./pages/VoorraadDetailPage'))
const VoorraadRapportagesPage = lazy(() => import('./pages/VoorraadRapportagesPage'))
const InventarisatiesPage = lazy(() => import('./pages/InventarisatiesPage'))
const UniversalInventoryPage = lazy(() => import('./pages/UniversalInventoryPage'))
const InventarisatieDetailPage = lazy(() => import('./pages/InventarisatieDetailPage'))
const DocumentenPage = lazy(() => import('./pages/DocumentenPage'))
const PlanningPage = lazy(() => import('./pages/PlanningPage'))
const PlanningCalendar = lazy(() => import('./pages/PlanningCalendar'))
const WerkbonnenPage = lazy(() => import('./pages/WerkbonnenPage'))
const MaterialenPage = lazy(() => import('./pages/MaterialenPage'))
const GrepenBeheerPage = lazy(() => import('./pages/GrepenBeheerPage'))
const LedStripBeheerPage = lazy(() => import('./pages/LedStripBeheerPage'))
const LeveranciersprijslijstenPage = lazy(() => import('./pages/LeveranciersprijslijstenPage'))
const PrijsvergelijkingPage = lazy(() => import('./pages/PrijsvergelijkingPage'))
const RapportPlanningPage = lazy(() => import('./pages/RapportPlanningPage'))
const MateriaalVerbruikRapportPage = lazy(() => import('./pages/MateriaalVerbruikRapportPage'))
const KasttypeAnalysePage = lazy(() => import('./pages/KasttypeAnalysePage'))
const SupplierPortalLoginPage = lazy(() => import('./pages/supplier-portal/SupplierPortalLoginPage'))
const SupplierPortalDashboard = lazy(() => import('./pages/supplier-portal/SupplierPortalDashboard'))
const SupplierOrderDetailPage = lazy(() => import('./pages/supplier-portal/SupplierOrderDetailPage'))
const SupplierDocumentenPage = lazy(() => import('./pages/supplier-portal/SupplierDocumentenPage'))
const SupplierWachtwoordResetPage = lazy(() => import('./pages/supplier-portal/SupplierWachtwoordResetPage'))
const SupplierTwoFactorPage = lazy(() => import('./pages/supplier-portal/SupplierTwoFactorPage'))
const GlobalMaterialLibraryPage = lazy(() => import('./pages/GlobalMaterialLibraryPage'))
const KleurenbankPage = lazy(() => import('./pages/KleurenbankPage'))
const InventoryManagementPage = lazy(() => import('./pages/InventoryManagementPage'))
const StorageLocationsPage = lazy(() => import('./pages/StorageLocationsPage'))
const RuimtesPage = lazy(() => import('./pages/RuimtesPage'))
const MasterDNAPage = lazy(() => import('./pages/MasterDNAPage'))
const KastConfiguratorPage = lazy(() => import('./pages/KastConfiguratorPage'))
// C8 (BLOK C / Sprint 1): KastenwizardV2 verwijderd — MasterDNA blijft voor defaults,
// kast-configuratie loopt via /kasten (KastConfiguratorPage).
const PivotDoorWizardPage = lazy(() => import('./pages/PivotDoorWizardPage'))
const ProductLibraryPage = lazy(() => import('./pages/ProductLibraryPage'))
const VerlofBeheerPage = lazy(() => import('./pages/VerlofBeheerPage'))
const BetalingsplanningPage = lazy(() => import('./pages/BetalingsplanningPage'))
const CashflowPage = lazy(() => import('./pages/CashflowPage'))
const CashFlowPulsePage = lazy(() => import('./pages/CashFlowPulsePage'))
const ProductiePage = lazy(() => import('./pages/ProductiePage'))
const ProductieDashboardPage = lazy(() => import('./pages/ProductieDashboardPage'))
const ProductieOrdersPage = lazy(() => import('./pages/ProductieOrdersPage'))
const NestingViewerPage = lazy(() => import('./pages/NestingViewerPage'))
const NestingDashboardPage = lazy(() => import('./pages/NestingDashboardPage'))
const VerkoopsovereenkomstPage = lazy(() => import('./pages/VerkoopsovereenkomstPage'))
const OfferteFichePage = lazy(() => import('./pages/OfferteFichePage'))
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'))
const SaaSOverview = lazy(() => import('./pages/admin/SaaSOverview'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const SupportDashboard = lazy(() => import('./pages/admin/SupportDashboard'))
const UiShowcasePage = lazy(() => import('./pages/UiShowcasePage'))  // R11 design system — visuele validatie
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'))
const SetupWizard = lazy(() => import('./pages/setup/SetupWizardPage'))
const FinanceDashboardPage = lazy(() => import('./pages/FinanceDashboardPage'))
const FinanceControlCenterPage = lazy(() => import('./pages/FinanceControlCenterPage'))
const OrderStatusPage = lazy(() => import('./pages/OrderStatusPage'))
const TakenOverzichtPage = lazy(() => import('./pages/TakenOverzichtPage'))
const LeadPipelinePage = lazy(() => import('./pages/LeadPipelinePage'))
const AiInboxPage = lazy(() => import('./pages/AiInboxPage'))
const DigitaleMedewerkersPage = lazy(() => import('./pages/DigitaleMedewerkersPage'))
const SalesDashboardPage = lazy(() => import('./pages/SalesDashboardPage'))
const SalesRepPortal = lazy(() => import('./pages/SalesRepPortal'))
const ManagerSalesAnalytics = lazy(() => import('./pages/ManagerSalesAnalytics'))
const CommissionOverview = lazy(() => import('./pages/CommissionOverview'))
const VerkoperDetailPage = lazy(() => import('./pages/VerkoperDetailPage'))
const GoedkeuringPage = lazy(() => import('./pages/GoedkeuringPage'))
const WerkbonDetailPage = lazy(() => import('./pages/WerkbonDetailPage'))
const TrackPage = lazy(() => import('./pages/TrackPage'))
const ViewOfferPage = lazy(() => import('./pages/portal/ViewOfferPage'))
const ViewProjectPage = lazy(() => import('./pages/portal/ViewProjectPage'))
const OrderAdvicePage = lazy(() => import('./pages/procurement/OrderAdvicePage'))
const ProcurementDashboard = lazy(() => import('./pages/ProcurementDashboard'))
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'))
const QuickStartPage = lazy(() => import('./pages/QuickStartPage'))
const SketchUpImportPage = lazy(() => import('./pages/import/SketchUpImportPage'))
const OrderImportPage = lazy(() => import('./pages/OrderImportPage'))
const ReceiptsInboxPage = lazy(() => import('./pages/ReceiptsInboxPage'))
const SawOperatorDashboard = lazy(() => import('./pages/SawOperatorDashboard'))
const StationScanner = lazy(() => import('./pages/StationScanner'))
const KittingDashboard = lazy(() => import('./pages/KittingDashboard'))
const TeamSettings = lazy(() => import('./pages/TeamSettings'))
const TimeSheetView = lazy(() => import('./pages/TimeSheetView'))
const NacalculatieDashboard = lazy(() => import('./pages/NacalculatieDashboard'))
const ProjectPLOverview = lazy(() => import('./pages/ProjectPLOverview'))
const ManagementReports = lazy(() => import('./pages/ManagementReports'))
const ProjectDeveloperDashboard = lazy(() => import('./pages/enterprise/ProjectDeveloperDashboard'))
const GlobalBatchingCockpit = lazy(() => import('./pages/enterprise/GlobalBatchingCockpit'))
const SortingStation = lazy(() => import('./pages/station/SortingStation'))
const Pricing = lazy(() => import('./pages/Pricing'))
const SetupPassword = lazy(() => import('./pages/SetupPassword'))
const BridgePage = lazy(() => import('./pages/BridgePage'))
const ManagementDashboard = lazy(() => import('./pages/ManagementDashboard'))
const InstallationSchedulingPage = lazy(() => import('./pages/InstallationSchedulingPage'))
const MontagePlanner = lazy(() => import('./pages/MontagePlanner'))
const AISettingsPage = lazy(() => import('./pages/settings/AISettingsPage'))
const MachinesPage = lazy(() => import('./pages/settings/MachinesPage'))
const SafetyVaultPage = lazy(() => import('./pages/settings/SafetyVaultPage'))
const SafetyDocumentFormPage = lazy(() => import('./pages/settings/SafetyDocumentFormPage'))
const FinancialsPage = lazy(() => import('./pages/settings/FinancialsPage'))
const DNAPage = lazy(() => import('./pages/settings/DNAPage'))
const BetalingsvoorwaardenPage = lazy(() => import('./pages/settings/BetalingsvoorwaardenPage'))
const BtwRegimesPage = lazy(() => import('./pages/settings/BtwRegimesPage'))
const SketchUpKoppelingPage = lazy(() => import('./pages/settings/SketchUpKoppelingPage'))
const BedrijfsProfielPage = lazy(() => import('./pages/instellingen/BedrijfsProfielPage'))
const QuoteBuilderPage = lazy(() => import('./pages/QuoteBuilderPage'))
const InvoicePage = lazy(() => import('./pages/financials/InvoicePage'))
const AgingReportPage = lazy(() => import('./pages/financials/AgingReportPage'))
const InkoopFacturenPage = lazy(() => import('./pages/financials/InkoopFacturenPage'))
const VisualProductionPage = lazy(() => import('./pages/VisualProductionPage'))
const MaterialAwareConfiguratorPage = lazy(() => import('./pages/MaterialAwareConfiguratorPage'))
const TeBestellenPage = lazy(() => import('./pages/TeBestellenPage'))
const PurchaseOrderManager = lazy(() => import('./pages/PurchaseOrderManager'))
const PurchasingDashboard = lazy(() => import('./pages/PurchasingDashboard'))
const ProductionRecapPage = lazy(() => import('./pages/ProductionRecapPage'))
const SustainabilityOverviewPage = lazy(() => import('./pages/SustainabilityOverviewPage'))
const LadderliftOverzichtPage = lazy(() => import('./pages/LadderliftOverzichtPage'))
const TerminalPage = lazy(() => import('./pages/terminal/TerminalPage'))
const SnelOfferteWizard = lazy(() => import('./components/SnelOfferte/SnelOfferteWizard'))

// Fallback tijdens lazy loading van pagina's
const PageFallback = () => {
  const { t } = useTranslation()
  return (
    <div className="min-h-[200px] flex items-center justify-center">
      <div className="animate-pulse text-gray-500">{t('app.pageFallbackLoading')}</div>
    </div>
  )
}

// Publieke resultaatpagina's voor goedkeuring (geen auth)
function GoedkeuringResultPage({ success }) {
  const { t } = useTranslation()
  const title = success ? t('app.goedkeuringSuccessTitle') : t('app.goedkeuringRejectedTitle')
  const message = success ? t('app.goedkeuringSuccessMessage') : t('app.goedkeuringRejectedMessage')
  const colorClass = success ? 'text-green-600' : 'text-red-600'
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className={`text-2xl font-bold mb-2 ${colorClass}`}>{title}</h1>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  )
}

// Publieke pagina na acceptatie offerte
function OfferAcceptedPage() {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-green-600 mb-2">{t('app.offerAcceptedTitle')}</h1>
        <p className="text-gray-600">{t('app.offerAcceptedMessage')}</p>
      </div>
    </div>
  )
}

function App() {
  const { isOpen, message, hide } = useUpgradeModalStore()
  return (
    <ErrorBoundary>
      {/* De enige Toaster: in de root, zodat ook portaal, wachtwoord-reset en mobile meldingen
          tonen (#694). Niet ook in Layout zetten, anders verschijnt elke melding dubbel. */}
      <Toaster position="top-right" />
      <UpgradeRequiredModal isOpen={isOpen} message={message} onClose={hide} />
      <PWAInstallPrompt />
      <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registreer" element={<RegisterPage />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/setup-password" element={<SetupPassword />} />
        <Route path="/setup" element={<ProtectedRoute><SetupWizard /></ProtectedRoute>} />
        <Route path="/bridge" element={<BridgePage />} />
        {/* Klantenportaal (publiek — geen auth) */}
        <Route path="/portaal" element={<KlantenPortaalPage />} />
        {/* Publieke goedkeuringsroutes (geen auth vereist) */}
        <Route path="/goedkeuring/:token" element={<GoedkeuringPage />} />
        <Route path="/goedkeuring/:token/success" element={<GoedkeuringResultPage success />} />
        <Route path="/goedkeuring/:token/rejected" element={<GoedkeuringResultPage success={false} />} />
        {/* Publieke Track & Trace route (geen auth vereist) */}
        <Route path="/track/:token" element={<TrackPage />} />
        {/* Publieke Client Portal routes (geen auth vereist) */}
        <Route path="/portal/quote/:token" element={<PortalQuoteRedirect />} />
        <Route path="/portal/view-offer" element={<ViewOfferPage />} />
        <Route path="/portal/view-project" element={<ViewProjectPage />} />
        <Route path="/portal/offer-accepted" element={<OfferAcceptedPage />} />
        
        {/* Leverancier Portaal routes (eigen auth, geen Orion layout) */}
        <Route path="/leverancier-portaal/login" element={<SupplierPortalLoginPage />} />
        <Route path="/leverancier-portaal/dashboard" element={<SupplierPortalDashboard />} />
        <Route path="/leverancier-portaal/orders/:id" element={<SupplierOrderDetailPage />} />
        <Route path="/leverancier-portaal/documenten" element={<SupplierDocumentenPage />} />
        <Route path="/leverancier-portaal/reset" element={<SupplierWachtwoordResetPage />} />
        <Route path="/leverancier-portaal/reset/:token" element={<SupplierWachtwoordResetPage />} />
        <Route path="/leverancier-portaal/2fa" element={<SupplierTwoFactorPage />} />

        {/* Mobile Routes (PWA/Installer Mode) */}
        <Route path="/mobile/*" element={<MobileLayout />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <OrionAIProvider>
              <Layout />
            </OrionAIProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
        <Route path="ui-showcase" element={<ErrorBoundary><UiShowcasePage /></ErrorBoundary>} />
        <Route path="klanten" element={<ErrorBoundary><KlantenPage /></ErrorBoundary>} />
        <Route path="klanten/:id" element={<ErrorBoundary><KlantDetailPage /></ErrorBoundary>} />
        <Route path="projecten-overzicht" element={<ErrorBoundary><ProjectenOverzichtPage /></ErrorBoundary>} />
        <Route path="projecten-dashboard" element={<ErrorBoundary><ProjectenDashboardPage /></ErrorBoundary>} />
        <Route path="projecten" element={<ErrorBoundary><ProjectenPage /></ErrorBoundary>} />
        <Route path="projecten/:projectId" element={<ErrorBoundary><ProjectDetailPage /></ErrorBoundary>} />
        <Route path="receipts/inbox" element={<ErrorBoundary><ReceiptsInboxPage /></ErrorBoundary>} />
        <Route path="projecten/:projectId/quote-builder" element={<ErrorBoundary><QuoteBuilderPage /></ErrorBoundary>} />
        <Route path="projecten/:projectId/configurator" element={<ErrorBoundary><MaterialAwareConfiguratorPage /></ErrorBoundary>} />
        <Route path="configurator" element={<ErrorBoundary><MaterialAwareConfiguratorPage /></ErrorBoundary>} />
        <Route path="projecten/:projectId/serviceoverzicht" element={<ErrorBoundary><ServiceoverzichtPage /></ErrorBoundary>} />
        <Route path="dossiers" element={<ErrorBoundary><DossiersPage /></ErrorBoundary>} />
        <Route path="billing" element={<ErrorBoundary><BillingPage /></ErrorBoundary>} />
        <Route path="leveranciers" element={<ErrorBoundary><LeveranciersPage /></ErrorBoundary>} />
        <Route path="merken-kortingen" element={<ErrorBoundary><MerkenKortingenPage /></ErrorBoundary>} />
        <Route path="apparatuur" element={<ErrorBoundary><ApparatuurPage /></ErrorBoundary>} />
        <Route path="offertes" element={<ErrorBoundary><OffertesPage /></ErrorBoundary>} />
        <Route path="offertes/:offerteId" element={<ErrorBoundary><OfferteClipboardProvider><OfferteFichePage /></OfferteClipboardProvider></ErrorBoundary>} />
        <Route path="snelofferte/nieuw" element={<ErrorBoundary><SnelOfferteWizard onSluiten={() => window.history.back()} /></ErrorBoundary>} />
        <Route path="facturen" element={<PermissionRoute resource="facturen" action="read"><ErrorBoundary><FacturenPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="facturen/:id" element={<PermissionRoute resource="facturen" action="read"><ErrorBoundary><InvoicePage /></ErrorBoundary></PermissionRoute>} />
        <Route path="bestellingen" element={<ErrorBoundary><BestellingenPage /></ErrorBoundary>} />
        <Route path="bestellingen/:id" element={<ErrorBoundary><BestellingDetailPage /></ErrorBoundary>} />
        <Route path="voorraad" element={<ErrorBoundary><VoorraadPage /></ErrorBoundary>} />
        <Route path="voorraad/:id" element={<ErrorBoundary><VoorraadDetailPage /></ErrorBoundary>} />
        <Route path="voorraad/rapportages" element={<ErrorBoundary><VoorraadRapportagesPage /></ErrorBoundary>} />
        <Route path="voorraad/inventarisaties" element={<ErrorBoundary><InventarisatiesPage /></ErrorBoundary>} />
        <Route path="magazijn" element={<ErrorBoundary><UniversalInventoryPage /></ErrorBoundary>} />
        <Route path="voorraad/inventarisaties/:id" element={<ErrorBoundary><InventarisatieDetailPage /></ErrorBoundary>} />
        <Route path="documenten" element={<ErrorBoundary><DocumentenPage /></ErrorBoundary>} />
        <Route path="planning" element={<ErrorBoundary><PlanningPage /></ErrorBoundary>} />
        <Route path="planning/montage" element={<ErrorBoundary><TierGuard requiredTier="MASTER"><MontagePlanner /></TierGuard></ErrorBoundary>} />
        <Route path="planning/calendar" element={<ErrorBoundary><PlanningCalendar /></ErrorBoundary>} />
        <Route path="planning/gantt" element={<ErrorBoundary><VisualProductionPage /></ErrorBoundary>} />
        <Route path="planning/weekkalender" element={<ErrorBoundary><GanttDragDrop /></ErrorBoundary>} />
        <Route path="planning/tweeling" element={<ErrorBoundary><DigitaleTweelingDashboard /></ErrorBoundary>} />
        <Route path="planning/ai-optimalisatie" element={<ErrorBoundary><div className="p-6"><AIPlanningPanel /></div></ErrorBoundary>} />
        <Route path="visual-production" element={<ErrorBoundary><VisualProductionPage /></ErrorBoundary>} />
        <Route path="procurement" element={<ErrorBoundary><ProcurementPage /></ErrorBoundary>} />
        <Route path="te-bestellen" element={<ErrorBoundary><TeBestellenPage /></ErrorBoundary>} />
        <Route path="procurement/purchase-orders" element={<ErrorBoundary><PurchaseOrderManager /></ErrorBoundary>} />
        <Route path="analytics/production-recap" element={<ErrorBoundary><ProductionRecapPage /></ErrorBoundary>} />
        <Route path="management" element={<ErrorBoundary><ManagementDashboard /></ErrorBoundary>} />
        <Route path="installation-scheduling" element={<ErrorBoundary><InstallationSchedulingPage /></ErrorBoundary>} />
        <Route path="verlofbeheer" element={<ErrorBoundary><VerlofBeheerPage /></ErrorBoundary>} />
        <Route path="procurement/order-advice" element={<ErrorBoundary><OrderAdvicePage /></ErrorBoundary>} />
        <Route path="procurement/purchasing" element={<ErrorBoundary><PurchasingDashboard /></ErrorBoundary>} />
        <Route path="werkbonnen" element={<ErrorBoundary><WerkbonnenPage /></ErrorBoundary>} />
        <Route path="werkbonnen/:id" element={<ErrorBoundary><WerkbonDetailPage /></ErrorBoundary>} />
        {/* Materialen: beheerpagina met tabel; library = plaat + beslag + samengesteld */}
        <Route path="materialen" element={<ErrorBoundary><MaterialenPage /></ErrorBoundary>} />
        <Route path="materialen/beheer" element={<ErrorBoundary><MaterialenPage /></ErrorBoundary>} />
        <Route path="materialen/grepen" element={<ErrorBoundary><GrepenBeheerPage /></ErrorBoundary>} />
        <Route path="materialen/ledstrips" element={<ErrorBoundary><LedStripBeheerPage /></ErrorBoundary>} />
        <Route path="materialen/leveranciersprijslijsten" element={<ErrorBoundary><LeveranciersprijslijstenPage /></ErrorBoundary>} />
        <Route path="leveranciers/prijsvergelijking" element={<ErrorBoundary><PrijsvergelijkingPage /></ErrorBoundary>} />
        <Route path="materialen/library" element={<ErrorBoundary><GlobalMaterialLibraryPage /></ErrorBoundary>} />
        <Route path="materialen/kleurenbank" element={<ErrorBoundary><KleurenbankPage /></ErrorBoundary>} />
        {/* Redirect oude bookmark: composition-builder → library */}
        <Route path="materialen/composition-builder" element={<Navigate to="/materialen/library" replace />} />
        <Route path="inventory" element={<ErrorBoundary><InventoryManagementPage /></ErrorBoundary>} />
        <Route path="storage-locations" element={<ErrorBoundary><StorageLocationsPage /></ErrorBoundary>} />
        <Route path="ruimtes" element={<ErrorBoundary><RuimtesPage /></ErrorBoundary>} />
        <Route path="quick-start" element={<ErrorBoundary><QuickStartPage /></ErrorBoundary>} />
        <Route path="import/sketchup" element={<ErrorBoundary><SketchUpImportPage /></ErrorBoundary>} />
        <Route path="orders/import" element={<ErrorBoundary><OrderImportPage /></ErrorBoundary>} />
        <Route path="master-dna" element={<PermissionRoute resource="instellingen" action="read"><ErrorBoundary><MasterDNAPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="kasten" element={<ErrorBoundary><KastConfiguratorPage /></ErrorBoundary>} />
        {/* C8: /kast-wizard route verwijderd (zie cleanup in App.jsx lazy-imports). */}
        <Route path="pivot-deur-wizard" element={<ErrorBoundary><PivotDoorWizardPage /></ErrorBoundary>} />
        <Route path="betalingsplanning" element={<ErrorBoundary><BetalingsplanningPage /></ErrorBoundary>} />
        <Route path="cashflow" element={<ErrorBoundary><CashflowPage /></ErrorBoundary>} />
        <Route path="productie" element={<ErrorBoundary><ProductiePage /></ErrorBoundary>} />
        <Route path="productie/orders" element={<ErrorBoundary><ProductieOrdersPage /></ErrorBoundary>} />
        <Route path="nesting/:batchId" element={<PermissionRoute resource="productie" action="read"><ErrorBoundary><NestingViewerPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="productie/nesting-stats" element={<PermissionRoute resource="productie" action="read"><ErrorBoundary><NestingDashboardPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="productie/dashboard" element={<ErrorBoundary><ProductieDashboardPage /></ErrorBoundary>} />
        <Route path="productie/dashboard/:projectId" element={<ErrorBoundary><ProductieDashboardPage /></ErrorBoundary>} />
        <Route path="productie/zaag" element={<PermissionRoute resource="productie" action="read"><ErrorBoundary><SawOperatorDashboard /></ErrorBoundary></PermissionRoute>} />
        <Route path="productie/station/:stationId" element={<ErrorBoundary><StationScanner /></ErrorBoundary>} />
        <Route path="productie/kitting/:projectId" element={<ErrorBoundary><KittingDashboard /></ErrorBoundary>} />
        <Route path="productie/time-sheet" element={<ErrorBoundary><TimeSheetView /></ErrorBoundary>} />
        <Route path="projecten/:projectId/nacalculatie" element={<ErrorBoundary><NacalculatieDashboard /></ErrorBoundary>} />
        <Route path="projecten/:projectId/pl-overview" element={<ErrorBoundary><TierGuard requiredTier="ENTERPRISE"><ProjectPLOverview /></TierGuard></ErrorBoundary>} />
        <Route path="management/reports" element={<ErrorBoundary><ManagementReports /></ErrorBoundary>} />
        <Route path="productie/kitting/:projectId/lane/:laneId" element={<ErrorBoundary><KittingDashboard /></ErrorBoundary>} />
        
        {/* Enterprise Module Routes */}
        <Route path="projecten/:projectId/enterprise" element={<ErrorBoundary><ProjectDeveloperDashboard /></ErrorBoundary>} />
        <Route path="productie/global-batching" element={<ErrorBoundary><GlobalBatchingCockpit /></ErrorBoundary>} />
        <Route path="station/sorting" element={<ErrorBoundary><SortingStation /></ErrorBoundary>} />
        <Route path="finance" element={<PermissionRoute resource="financieel" action="read"><ErrorBoundary><FinanceDashboardPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="finance/control-center" element={<PermissionRoute resource="financieel" action="read"><ErrorBoundary><FinanceControlCenterPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="finance/pulse" element={<PermissionRoute resource="financieel" action="read"><ErrorBoundary><CashFlowPulsePage /></ErrorBoundary></PermissionRoute>} />
        <Route path="order-status" element={<ErrorBoundary><OrderStatusPage /></ErrorBoundary>} />
        <Route path="ladderlift" element={<ErrorBoundary><LadderliftOverzichtPage /></ErrorBoundary>} />
        <Route path="sustainability" element={<ErrorBoundary><SustainabilityOverviewPage /></ErrorBoundary>} />
        <Route path="taken" element={<ErrorBoundary><TakenOverzichtPage /></ErrorBoundary>} />
        <Route path="leads" element={<ErrorBoundary><LeadPipelinePage /></ErrorBoundary>} />
        <Route path="ai-inbox" element={<PermissionRoute resource="ai_voorstellen" action="read"><ErrorBoundary><AiInboxPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="digitale-medewerkers" element={<PermissionRoute resource="agents" action="read"><ErrorBoundary><DigitaleMedewerkersPage /></ErrorBoundary></PermissionRoute>} />
        <Route path="sales" element={<ErrorBoundary><SalesDashboardPage /></ErrorBoundary>} />
        <Route path="sales/rep-portal" element={<ErrorBoundary><SalesRepPortal /></ErrorBoundary>} />
        <Route path="sales/verkoper/:verkoperId" element={<ErrorBoundary><VerkoperDetailPage /></ErrorBoundary>} />
        <Route path="analytics/sales" element={<ErrorBoundary><ManagerSalesAnalytics /></ErrorBoundary>} />
        <Route path="commissions" element={<ErrorBoundary><CommissionOverview /></ErrorBoundary>} />
        <Route path="offertes/:offerteId/verkoopsovereenkomst" element={<ErrorBoundary><VerkoopsovereenkomstPage /></ErrorBoundary>} />
        <Route path="kasten/configurator" element={<ErrorBoundary><KastConfiguratorPage /></ErrorBoundary>} />
        <Route path="bibliotheek" element={<ErrorBoundary><ProductLibraryPage /></ErrorBoundary>} />
        
        {/* Admin Routes */}
        <Route path="admin" element={<SuperAdminRoute><ErrorBoundary><AdminDashboard /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="admin/support" element={<SuperAdminRoute><ErrorBoundary><SupportDashboard /></ErrorBoundary></SuperAdminRoute>} />
        <Route path="admin/users" element={<PermissionRoute resource="gebruikers" action="manage"><UserManagementPage /></PermissionRoute>} />
        <Route path="admin/team" element={<PermissionRoute resource="gebruikers" action="read"><ErrorBoundary><TeamSettings /></ErrorBoundary></PermissionRoute>} />
        <Route path="admin/audit" element={<PermissionRoute resource="admin" action="read"><AuditLogPage /></PermissionRoute>} />
        <Route path="admin/saas" element={<SaaSOverview />} />
        
        {/* Settings Routes */}
        <Route path="settings/company" element={<Navigate to="/master-dna?tab=identity" replace />} />
        <Route path="settings/ai" element={<ErrorBoundary><AISettingsPage /></ErrorBoundary>} />
              <Route path="settings/btw" element={<ErrorBoundary><BtwRegimesPage /></ErrorBoundary>} />
        <Route path="settings/machines" element={<ErrorBoundary><MachinesPage /></ErrorBoundary>} />
        <Route path="settings/sketchup" element={<ErrorBoundary><SketchUpKoppelingPage /></ErrorBoundary>} />
        <Route path="settings/safety-vault" element={<ErrorBoundary><SafetyVaultPage /></ErrorBoundary>} />
        <Route path="instellingen/profiel" element={<ErrorBoundary><BedrijfsProfielPage /></ErrorBoundary>} />
        <Route path="instellingen/rapporten" element={<ErrorBoundary><RapportPlanningPage /></ErrorBoundary>} />
        <Route path="rapporten/materiaalverbruik" element={<ErrorBoundary><MateriaalVerbruikRapportPage /></ErrorBoundary>} />
        <Route path="rapporten/kasttype-analyse" element={<ErrorBoundary><KasttypeAnalysePage /></ErrorBoundary>} />
        <Route path="settings/safety-vault/document/:documentId/fill" element={<ErrorBoundary><SafetyDocumentFormPage /></ErrorBoundary>} />
        <Route path="settings/financials" element={<Navigate to="/master-dna?tab=finance&subtab=boekhouding" replace />} />
        <Route path="settings/dna" element={<ErrorBoundary><DNAPage /></ErrorBoundary>} />
        <Route path="settings/betalingsvoorwaarden" element={<ErrorBoundary><BetalingsvoorwaardenPage /></ErrorBoundary>} />
        <Route path="settings/wip-formulas" element={<ErrorBoundary><InventoryManagementPage initialTab="wip" /></ErrorBoundary>} />
        <Route path="financials/aging-report" element={<ErrorBoundary><AgingReportPage /></ErrorBoundary>} />
        <Route path="financials/inkoop" element={<ErrorBoundary><InkoopFacturenPage /></ErrorBoundary>} />
        <Route path="financials/invoices/:id" element={<ErrorBoundary><InvoicePage /></ErrorBoundary>} />
        <Route path="financials/invoices/new" element={<ErrorBoundary><InvoicePage /></ErrorBoundary>} />
        
        {/* Analytics Routes */}
        <Route 
          path="analytics/production-recap" 
          element={
            <ErrorBoundary>
              <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}>
                <ProductionRecapPage />
              </RoleProtectedRoute>
            </ErrorBoundary>
          } 
        />
        
        {/* Terminal Routes */}
        <Route path="terminal/:workstation_id" element={<ErrorBoundary><TerminalPage /></ErrorBoundary>} />
      </Route>
    </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

export default App
